// frontend/src/components/Header.jsx

import React, { useState, useRef, useEffect, useMemo } from "react";
import { useAuth } from "../lib/auth";
import LoginModal from "./LoginModal";
import snapgradLogo from "../assets/SnapGrad-By-Snaphomz_01.png";

const NAV_LINKS = [
  { id: "overview", label: "Overview" },
  { id: "stem", label: "Best For STEM" },
  { id: "athletes", label: "Best For Athletes" },
];

const looksLikeUuid = (value) => {
  const s = (value || "").trim();
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
};

const prettyNameFromEmail = (email) => {
  const safe = (email || "").trim();
  if (!safe.includes("@")) return "";
  const local = safe.split("@")[0] || "";
  return local
    .split(/[._-]+/g)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
};

const getInitials = (nameOrEmail) => {
  const s = (nameOrEmail || "").trim();
  if (!s) return "";
  // if it's email, use local part
  const base = s.includes("@") ? s.split("@")[0] : s;
  const parts = base.split(/\s+/).filter(Boolean);

  if (parts.length === 1) {
    // "harish.snaphomz" -> HS
    const chunks = parts[0].split(/[._-]+/).filter(Boolean);
    if (chunks.length >= 2) return (chunks[0][0] + chunks[1][0]).toUpperCase();
    return parts[0].slice(0, 2).toUpperCase();
  }

  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const Header = ({
  activeView = "overview",
  onViewChange,
  loginModalOpen,
  onRequestLoginModal,
  onCloseLoginModal,
}) => {
  const { isSignedIn, userName, userEmail, userPicture, signOut } = useAuth();

  const isControlled = typeof loginModalOpen === "boolean";
  const [internalLoginModalOpen, setInternalLoginModalOpen] = useState(false);
  const resolvedLoginModalOpen = isControlled ? loginModalOpen : internalLoginModalOpen;
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  // ✅ NEW: if profile image fails, switch to initials avatar
  const [avatarError, setAvatarError] = useState(false);

  const userMenuRef = useRef(null);

  const storedSnapgradUser = useMemo(() => {
    try {
      const raw = localStorage.getItem("snapgrad_user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, [isSignedIn]);

  const displayName = useMemo(() => {
    const candidates = [
      userName,
      storedSnapgradUser?.name,
      userEmail,
      storedSnapgradUser?.email,
    ]
      .map((v) => (typeof v === "string" ? v.trim() : ""))
      .filter(Boolean);

    // Prefer something that is NOT a UUID
    const nonUuid = candidates.find((v) => !looksLikeUuid(v));
    if (nonUuid) {
      if (nonUuid.includes("@")) return prettyNameFromEmail(nonUuid) || nonUuid;
      return nonUuid;
    }

    // If everything looks like UUID, fallback to prettified email
    const email = userEmail || storedSnapgradUser?.email || "";
    return prettyNameFromEmail(email) || "Account";
  }, [userName, userEmail, storedSnapgradUser]);

  const initials = useMemo(() => {
    const base = displayName || userEmail || storedSnapgradUser?.email || "";
    return getInitials(base) || "U";
  }, [displayName, userEmail, storedSnapgradUser]);

  const handleLoginClick = () => {
    if (!isControlled) setInternalLoginModalOpen(true);
    onRequestLoginModal?.();
  };
  const handleCloseModal = () => {
    if (!isControlled) setInternalLoginModalOpen(false);
    onCloseLoginModal?.();
  };
  const handleUserIconClick = () => setIsUserMenuOpen((v) => !v);

  const handleSignOut = async () => {
    try {
      await signOut();
    } finally {
      setIsUserMenuOpen(false);
      if (!isControlled) setInternalLoginModalOpen(false);
      onCloseLoginModal?.();
      setAvatarError(false);
    }
  };

  // Auto-close login modal once signed in
  useEffect(() => {
    if (isSignedIn && resolvedLoginModalOpen) handleCloseModal();
  }, [isSignedIn, resolvedLoginModalOpen]);

  // ✅ Reset avatar error when picture/user changes
  useEffect(() => {
    setAvatarError(false);
  }, [userPicture, userEmail, userName]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isUserMenuOpen]);

  return (
    <header className="bg-white border-b border-gray-200 py-3 sm:py-4">
      <div className="flex items-center justify-between w-full px-4 sm:px-8 lg:px-[133px] mx-auto">
        <div className="flex items-center space-x-4 flex-shrink-0">
          <div className="flex items-center cursor-default select-none">
            <img
              src={snapgradLogo}
              alt="SnapGrad Logo"
              className="h-8 w-auto max-w-[170px] object-contain sm:h-10 sm:max-w-[240px] md:h-10 lg:h-11"
            />
          </div>
        </div>

        <nav className="hidden md:flex items-center space-x-12">
          {NAV_LINKS.map((tab) => {
            const isActive = tab.id === activeView;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => onViewChange?.(tab.id)}
                className={`relative pb-2 text-base font-medium transition-colors ${
                  isActive ? "text-[#F07639]" : "text-[#9A9A9A]"
                }`}
              >
                {tab.label}
                {isActive && (
                  <span className="absolute left-1/2 bottom-0 block h-[3px] w-10 -translate-x-1/2 bg-[#F07639]" />
                )}
              </button>
            );
          })}
        </nav>

        <div className="flex items-center space-x-4">
          {!isSignedIn ? (
            <button
              onClick={handleLoginClick}
              className="flex-shrink-0 whitespace-nowrap rounded-lg bg-[#F36F21] px-3 py-2 text-xs font-medium text-white transition-colors hover:bg-[#d85f1c] sm:px-4 sm:text-sm"
              type="button"
            >
              Login / Sign Up
            </button>
          ) : (
            <div className="flex items-center space-x-3 relative" ref={userMenuRef}>
              <span className="text-sm text-gray-700 max-w-[260px] truncate">
                {displayName}
              </span>

              <button
                onClick={handleUserIconClick}
                className="w-8 h-8 rounded-full overflow-hidden hover:ring-2 hover:ring-[#F36F21] transition-all flex items-center justify-center"
                aria-label="User menu"
                type="button"
              >
                {userPicture && !avatarError ? (
                  <img
                    src={userPicture}
                    alt=""
                    className="w-full h-full object-cover"
                    referrerPolicy="no-referrer"
                    onError={() => setAvatarError(true)}
                  />
                ) : (
                  <div className="w-full h-full bg-gray-200 flex items-center justify-center text-xs font-semibold text-gray-700">
                    {initials}
                  </div>
                )}
              </button>

              {isUserMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50 top-full overflow-hidden">
                  {(userEmail || storedSnapgradUser?.email) && (
                    <div className="px-4 py-3 text-xs text-gray-500 border-b border-gray-100 truncate">
                      {userEmail || storedSnapgradUser?.email}
                    </div>
                  )}

                  <button
                    onClick={handleSignOut}
                    className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                    type="button"
                  >
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Mobile nav */}
      <div className="md:hidden mx-auto mt-4 w-full px-4 sm:px-8 lg:px-[133px]">
        <nav className="flex justify-center gap-6 overflow-x-auto pb-1">
          {NAV_LINKS.map((tab) => {
            const isActive = tab.id === activeView;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => onViewChange?.(tab.id)}
                className={`relative pb-2 text-sm font-medium whitespace-nowrap ${
                  isActive ? "text-[#F07639]" : "text-[#9A9A9A]"
                }`}
              >
                {tab.label}
                {isActive && (
                  <span className="absolute left-1/2 bottom-0 block h-[2px] w-8 -translate-x-1/2 bg-[#F07639]" />
                )}
              </button>
            );
          })}
        </nav>
      </div>

      <LoginModal isOpen={resolvedLoginModalOpen} onClose={handleCloseModal} />
    </header>
  );
};

export default Header;

